import pluralize from "common/utils/pluralize";
import { DEFAULT_LOCATION_SETTINGS, LOCATION_SETTINGS_CONFIG, LOCATION_TYPES_ORDER } from "../builders/builderTypes";
import type { BuilderLocationSettings, LocationType } from "../builders/builderTypes";

export interface LocationDisplayInfo {
  displayName: string;
  icon: string;
  defaultLabel: string;
  examples: string;
  sortId: number;
}

/**
 * Get display information for a location type, respecting builder customization
 */
export function getLocationDisplayInfo(
  locationType: LocationType,
  builderSettings?: BuilderLocationSettings | null,
): LocationDisplayInfo {
  const config = LOCATION_SETTINGS_CONFIG[locationType];
  const customSettings = builderSettings?.[locationType];
  const sortId = LOCATION_TYPES_ORDER.indexOf(locationType);

  // Defensive check: this should never happen, but guard against runtime issues (bad data, type assertions, etc.)
  if (sortId === -1 || !config) {
    console.error(
      `Invalid location type "${locationType}" - not found in LOCATION_TYPES_ORDER or LOCATION_SETTINGS_CONFIG`,
    );
    // Return a safe fallback with sortId 999 to put invalid types at the end
    return {
      displayName: customSettings?.displayName || String(locationType),
      icon: customSettings?.icon || "location",
      defaultLabel: String(locationType),
      examples: customSettings?.examples || "",
      sortId: sortId === -1 ? 999 : sortId,
    };
  }

  return {
    displayName: customSettings?.displayName || config.label,
    icon: customSettings?.icon || config.defaultIcon,
    defaultLabel: config.label,
    examples: customSettings?.examples || config.example,
    sortId,
  };
}

/**
 * Get all enabled location types for a builder, ordered by hierarchy
 */
export function getEnabledLocationTypes(builderSettings?: BuilderLocationSettings | null): LocationType[] {
  return LOCATION_TYPES_ORDER.filter((type) => isLocationTypeEnabled(type, builderSettings));
}

/**
 * Get valid child types for a given parent location type
 * Returns all enabled types with higher sortId than parent
 */
export function getValidChildTypes(
  parentType: LocationType,
  builderSettings?: BuilderLocationSettings | null,
): LocationType[] {
  const parentInfo = getLocationDisplayInfo(parentType, builderSettings);
  const enabledTypes = getEnabledLocationTypes(builderSettings);

  return enabledTypes.filter((type) => {
    const restrictedParentTypes = LOCATION_SETTINGS_CONFIG[type]?.restrictedParentTypes;
    if (restrictedParentTypes && !restrictedParentTypes.includes(parentType)) {
      return false;
    }
    const childInfo = getLocationDisplayInfo(type, builderSettings);
    return childInfo.sortId > parentInfo.sortId;
  });
}

/**
 * Get available location types that can be created
 * If parentType is provided, returns valid child types
 * If no parent, returns all enabled types (top-level creation)
 */
export function getAvailableLocationTypes(
  parentType: LocationType | undefined | null,
  builderSettings?: BuilderLocationSettings | null,
): LocationType[] {
  if (parentType) {
    return getValidChildTypes(parentType, builderSettings);
  }
  return getEnabledLocationTypes(builderSettings);
}

/**
 * Get the highest enabled location type (top of hierarchy)
 */
export function getTopLocationLevel(builderSettings?: BuilderLocationSettings | null): LocationType | null {
  const enabled = getEnabledLocationTypes(builderSettings);
  return enabled.length > 0 ? enabled[0] : null;
}

/**
 * Check if a location type is enabled for a builder
 * Falls back to DEFAULT_LOCATION_SETTINGS when builderSettings is null/undefined
 */
export function isLocationTypeEnabled(
  locationType: LocationType,
  builderSettings?: BuilderLocationSettings | null,
): boolean {
  const settings = builderSettings ?? DEFAULT_LOCATION_SETTINGS;
  return settings[locationType]?.enabled ?? false;
}

/**
 * Get valid parent types for a given child location type
 * Returns all enabled types with lower sortId than child, or the type's
 * `restrictedParentTypes` from LOCATION_SETTINGS_CONFIG when it has one.
 */
export function getValidParentTypes(
  childType: LocationType,
  builderSettings?: BuilderLocationSettings | null,
): LocationType[] {
  const restrictedParentTypes = LOCATION_SETTINGS_CONFIG[childType]?.restrictedParentTypes;
  if (restrictedParentTypes) {
    return restrictedParentTypes.filter((type) => isLocationTypeEnabled(type, builderSettings));
  }

  // Default behavior for other types
  const childInfo = getLocationDisplayInfo(childType, builderSettings);
  const enabledTypes = getEnabledLocationTypes(builderSettings);

  return enabledTypes.filter((type) => {
    const parentInfo = getLocationDisplayInfo(type, builderSettings);
    return parentInfo.sortId < childInfo.sortId;
  });
}

/**
 * Check if parentType can be a parent of childType
 * Validates both are enabled and parent has lower sortId, or is one of
 * childType's `restrictedParentTypes` from LOCATION_SETTINGS_CONFIG when it has one.
 */
export function canBeParentOf(
  parentType: LocationType,
  childType: LocationType,
  builderSettings?: BuilderLocationSettings | null,
): boolean {
  if (!isLocationTypeEnabled(parentType, builderSettings) || !isLocationTypeEnabled(childType, builderSettings)) {
    return false;
  }

  const restrictedParentTypes = LOCATION_SETTINGS_CONFIG[childType]?.restrictedParentTypes;
  if (restrictedParentTypes) {
    return restrictedParentTypes.includes(parentType);
  }

  // Default sortId-based validation
  const parentInfo = getLocationDisplayInfo(parentType, builderSettings);
  const childInfo = getLocationDisplayInfo(childType, builderSettings);

  return parentInfo.sortId < childInfo.sortId;
}

/**
 * Get label for location type with optional pluralization
 * Uses pluralize utility to handle standard English pluralization rules
 */
export function getLocationTypeLabel(
  locationType: LocationType,
  builderSettings?: BuilderLocationSettings | null,
  plural: boolean = false,
): string {
  const { displayName } = getLocationDisplayInfo(locationType, builderSettings);
  return plural ? pluralize(displayName) : displayName;
}

/**
 * Get formatted location type label from a location object
 * Safely extracts type from location.locationLevel.type and formats it
 * We standardize on title case for location type labels (e.g., "Multi-Unit", "Division").
 *
 * @param location - Location object with optional locationLevel.type
 * @param builderSettings - Builder-specific location settings
 * @param options - Formatting options
 * @param options.lowercase - Deprecated: prefer title case everywhere. If true, returns lowercase label.
 * @param options.plural - If true, returns pluralized label (default: false)
 * @param options.fallback - Fallback text if type not found (default: "location")
 * @returns Formatted location type label or fallback
 */
export function getLocationTypeLabelFromLocation(
  location: { locationLevel?: { type?: LocationType } } | null | undefined,
  builderSettings?: BuilderLocationSettings | null,
  options?: { lowercase?: boolean; plural?: boolean; fallback?: string },
): string {
  const locationType = location?.locationLevel?.type;
  if (!locationType) {
    return options?.fallback ?? "location";
  }
  const label = getLocationTypeLabel(locationType, builderSettings, options?.plural ?? false);
  return options?.lowercase ? label.toLowerCase() : label;
}

/**
 * Get the display label of a location object.
 *
 * Convenience wrapper around getLocationTypeLabelFromLocation() that returns an empty string
 * as fallback for null/undefined locations. Useful when you want to safely display a location
 * type label without showing "location" as fallback text.
 *
 * @param location - Location object with optional locationLevel.type
 * @param builderSettings - Optional builder settings for custom names (if not provided, uses default names)
 * @param plural - Whether to return plural form (default: false)
 * @returns The location type display label (e.g., "Division", "Divisions", or custom name), or "" if no location
 *
 * @example
 * getLocationLevelLabel(myDivision) // "Division"
 * getLocationLevelLabel(myDivision, null, true) // "Divisions"
 * getLocationLevelLabel(myDivision, customSettings) // "Market" (if builder renamed it)
 * getLocationLevelLabel(null) // "" (empty string)
 */
export function getLocationLevelLabel(
  location: { locationLevel?: { type?: LocationType } } | null | undefined,
  builderSettings?: BuilderLocationSettings | null,
  plural: boolean = false,
): string {
  return getLocationTypeLabelFromLocation(location, builderSettings, { plural, fallback: "" });
}

/**
 * Get the plan name for a location (e.g., "Division Plan", "Division Plans")
 *
 * This is useful for generating human-readable plan names based on location type.
 * Respects builder customization (e.g., "Market Plan" if builder renamed Division to Market).
 * Can be used on both frontend and backend (e.g., in emails, status messages, UI labels).
 *
 * Note: Multi-unit locations always return singular ("Plan") since they are restricted to one plan.
 *
 * @param location - Location object with optional locationLevel.type
 * @param builderSettings - Optional builder settings for custom names (if not provided, uses default names)
 * @param plural - Whether to return plural form (ignored for multiunit locations)
 * @returns The formatted plan name (e.g., "Division Plan", "Community Plans")
 *
 * @example
 * getLocationPlanName(myDivision) // "Division Plan"
 * getLocationPlanName(myDivision, null, true) // "Division Plans"
 * getLocationPlanName(myMultiunit, null, true) // "Multi-Unit Plan" (always singular)
 * getLocationPlanName(myDivision, customSettings) // "Market Plan" (if renamed)
 * getLocationPlanName(null) // "location Plan" (fallback)
 */
export function getLocationPlanName(
  location: { locationLevel?: { type?: LocationType } } | null | undefined,
  builderSettings?: BuilderLocationSettings | null,
  plural: boolean = false,
): string {
  const locationLabel = getLocationTypeLabelFromLocation(location, builderSettings, { plural: false, fallback: "" });
  // Multi-unit locations are restricted to a single plan, always use singular
  const planWord = plural && !isMultiUnitLocation(location) ? "Plans" : "Plan";
  return locationLabel ? `${locationLabel} ${planWord}` : `location ${planWord}`;
}

/**
 * True when the location is a multi-unit location (e.g., a townhome building).
 * Structurally typed so it accepts any `Client*Location` shape with `locationLevel.type`.
 */
export function isMultiUnitLocation(
  location: { locationLevel?: { type?: LocationType | null } | null } | null | undefined,
): boolean {
  return location?.locationLevel?.type === "multiunit";
}
